import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UITable,
} from "@/components/ui/table";
import { PCDBPart, VMRSPart } from "@/types/parts";
import { AlertCircle, Code, Copy, Database, Loader2 } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import "./PartsDisplay.css";

interface PartsDisplayProps {
  pcdbParts: PCDBPart[];
  vmrsParts: VMRSPart[];
  pt?: string; // Predii Taxonomy data
  isLoading?: boolean;
  error?: string;
}

const PartsDisplay: React.FC<PartsDisplayProps> = ({
  pcdbParts,
  vmrsParts,
  pt,
  isLoading = false,
  error,
}) => {
  const pcdbPartsCount = pcdbParts.length;
  const vmrsPartsCount = vmrsParts.length;
  const totalParts = pcdbPartsCount + vmrsPartsCount;

  const handleCopyPartId = async (id: string, type: "PCDB" | "VMRS") => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success(`${type} ID copied to clipboard`);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="parts-display-loading">
        <div className="loading-header">
          <Loader2 size={20} className="animate-spin" />
          <span>Searching...</span>
        </div>
        <div className="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="parts-display-error">
        <AlertCircle size={24} className="error-icon" />
        <h3>Unable to Load Parts</h3>
        <p>{error}</p>
      </div>
    );
  }

  // Empty State
  if (totalParts === 0) {
    return (
      <div className="parts-display-empty">
        <Database size={48} className="empty-icon" />
        <h3>No Parts Found</h3>
        <p>
          Try adjusting your search terms or vehicle information to find
          relevant parts.
        </p>
      </div>
    );
  }

  return (
    <div className="modern-parts-display professional">
      {/* Predii Taxonomy Information */}
      {pt && (
        <div className="pt-info-section">
          <div className="pt-header">
            <div className="pt-icon">
              <Database size={16} />
            </div>
            <div className="pt-content">
              <h4 className="pt-title">Predii Component Term</h4>
              <p className="pt-description">{pt}</p>
            </div>
          </div>
        </div>
      )}

      <div className="professional-table-container">
        {pcdbParts.length > 0 && (
          <div className="table-section">
            <div className="section-header pcdb">
              <div className="header-icon">
                <Database size={16} />
              </div>
              <div className="header-text">
                <h3>PCdb Parts</h3>
                <span className="part-count">{pcdbPartsCount} parts</span>
              </div>
            </div>

            <UITable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-64">Part Terminology ID</TableHead>
                  <TableHead className="w-64">Part Terminology Name</TableHead>
                  <TableHead className="w-80">Category</TableHead>
                  <TableHead className="w-80">Sub Category</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pcdbParts.map((part) => (
                  <TableRow key={part.part_terminology_id}>
                    <TableCell>
                      <div className="table-id-cell pcdb">
                        <Database size={12} />
                        <code>{part.part_terminology_id}</code>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {part.part_terminology_name}
                    </TableCell>
                    <TableCell>{part.category}</TableCell>
                    <TableCell>
                      {part.sub_category || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        className="table-copy-btn"
                        onClick={() =>
                          handleCopyPartId(
                            part.part_terminology_id.toString(),
                            "PCDB"
                          )
                        }
                        aria-label="Copy part ID"
                      >
                        <Copy size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </UITable>
          </div>
        )}

        {vmrsPartsCount > 0 && (
          <div className="table-section">
            <div className="section-header vmrs">
              <div className="header-icon">
                <Code size={16} />
              </div>
              <div className="header-text">
                <h3>VMRS Parts</h3>
                <span className="part-count">{vmrsPartsCount} parts</span>
              </div>
            </div>

            <UITable>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">VMRS Code</TableHead>
                  <TableHead className="w-96">Part Description</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vmrsParts.map((part, index) => (
                  <TableRow key={`${part.vmrs_code}-${index}`}>
                    <TableCell>
                      <div className="table-id-cell vmrs">
                        <Code size={12} />
                        <code>{part.vmrs_code}</code>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {part.part_description}
                    </TableCell>
                    <TableCell>
                      <button
                        className="table-copy-btn"
                        onClick={() => handleCopyPartId(part.vmrs_code, "VMRS")}
                        aria-label="Copy VMRS code"
                      >
                        <Copy size={12} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </UITable>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartsDisplay;
